import { useEffect, useState } from "react";
import { api } from "../api";
import type { ProfilePage } from "../api/pages";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { PageHeader } from "../components/ui/page-header";
import { AccessChart } from "../components/ui/chart";
import { navigate, usePreviousPage } from "../lib/router";
import { ExternalLink, Edit3, BarChart3, Eye } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";

type PageAccessData = {
  date: string;
  accesses: number;
};

type PeriodOption = "7d" | "15d" | "30d" | "6m" | "1y";

const PERIOD_OPTIONS = [
  { value: "7d" as const, label: "7 dias" },
  { value: "15d" as const, label: "15 dias" },
  { value: "30d" as const, label: "30 dias" },
  { value: "6m" as const, label: "6 meses" },
  { value: "1y" as const, label: "1 ano" },
];

export function PageDetails({ slug }: { slug: string }) {
  const { t } = useLanguage();
  const [page, setPage] = useState<ProfilePage | null>(null);
  const [accessData, setAccessData] = useState<PageAccessData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>("7d");
  const previousPage = usePreviousPage();

  useEffect(() => {
    let active = true;

    // Load page data
    api.getMyPage(slug!).then(res => {
      if (!active) return;
      if (!res.ok) {
        setError(res.error);
        if (res.error.toLowerCase().includes("autenticado")) {
          navigate("/login");
        }
      } else if (!res.data) {
        setError(t.pageDetails.notFound);
      } else {
        setPage(res.data);
      }
      setLoading(false);
    });

    // Load access data from API when selected period changes

    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    let active = true;

    // Load access data from API
    api.getPageStats(slug!, selectedPeriod).then(res => {
      if (!active) return;
      if (res.ok && res.data) {
        setAccessData(res.data);
      } else {
        // Fallback to empty data if API fails
        setAccessData([]);
      }
    });

    return () => {
      active = false;
    };
  }, [slug, selectedPeriod]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white selection:bg-indigo-100 selection:text-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>
        <div className="text-center space-y-4">
          <div className="animate-spin mx-auto w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full"></div>
          <p className="text-slate-600 font-medium">{t.pageDetails.loading}</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <p className="text-2xl font-semibold text-neutral-800">{t.pageDetails.notFound}</p>
        <p className="mt-2 text-neutral-500 max-w-sm">{error}</p>
        <Button className="mt-6" onClick={() => navigate("/app")}>
          {t.pageDetails.backToHome}
        </Button>
      </div>
    );
  }

  const publicUrl = typeof window === "undefined" ? `/${page.slug}` : `${window.location.origin}/${page.slug}`;

  // Get total period days
  const getPeriodDays = (period: PeriodOption): number => {
    switch (period) {
      case "7d": return 7;
      case "15d": return 15;
      case "30d": return 30;
      case "6m": return 180;
      case "1y": return 365;
      default: return 7;
    }
  };

  // Get summary labels based on period
  const getSummaryLabels = (period: PeriodOption) => {
    const days = getPeriodDays(period);
    if (days <= 30) {
      return {
        total: t.pageDetails.total.replace("{period}", t.pageDetails.periods[period]),
        average: t.pageDetails.dailyAverage
      };
    } else if (days === 180) {
      return {
        total: t.pageDetails.total.replace("{period}", t.pageDetails.periods["6m"]),
        average: t.pageDetails.monthlyAverage
      };
    } else {
      return {
        total: t.pageDetails.total.replace("{period}", t.pageDetails.periods["1y"]),
        average: t.pageDetails.monthlyAverage
      };
    }
  };

  // Calculate values based on period
  const calculateValues = (period: PeriodOption) => {
    const total = accessData.reduce((sum, d) => sum + d.accesses, 0);
    const days = getPeriodDays(period);

    let average: number;
    let averageLabel: string;

    if (days <= 30) {
      average = Math.round(total / days);
      averageLabel = t.pageDetails.dailyAverage;
    } else if (days === 180) {
      average = Math.round(total / 6);
      averageLabel = t.pageDetails.monthlyAverage;
    } else {
      average = Math.round(total / 12);
      averageLabel = t.pageDetails.monthlyAverage;
    }

    return { total, average, averageLabel };
  };

  // Determine where to go back based on previous page
  const getBackDestination = () => {
    if (previousPage === "/app") {
      return "/app";
    }
    // If coming from page builder, go back to dashboard
    if (previousPage?.startsWith("/app/pagina/")) {
      return "/app";
    }
    // Default fallback
    return "/app";
  };

  const { total, average, averageLabel } = calculateValues(selectedPeriod);
  const periodLabels = getSummaryLabels(selectedPeriod);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 py-12 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <PageHeader
          label={t.pageDetails.pageInfo}
          title={page.title || `/${page.slug}`}
          backTo={getBackDestination()}
        />

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            onClick={() => window.open(publicUrl, '_blank')}
            variant="primary"
            icon={<ExternalLink className="w-4 h-4" />}
          >
            {t.pageDetails.openPage}
          </Button>
          <Button
            onClick={() => navigate(`/app/pagina/${page.slug}`)}
            variant="ghost"
            icon={<Edit3 className="w-4 h-4" />}
          >
            {t.pageDetails.editPage}
          </Button>
        </div>

        {/* Page Info */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Eye className="h-4 w-4" />
              <span>{page.today_accesses || 0} {(page.today_accesses || 0) === 1 ? t.pageDetails.accessToday : t.pageDetails.accessesToday}</span>
            </div>

            {page.description && (
              <div>
                <h3 className="font-medium text-neutral-900 mb-2">{t.pageDetails.description}</h3>
                <p className="text-neutral-600 whitespace-pre-wrap">{page.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
              <div>
                <h4 className="text-sm font-medium text-neutral-500">{t.pageDetails.address}</h4>
                <p className="text-neutral-900 font-mono">/{page.slug}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-neutral-500">{t.pageDetails.lastUpdate}</h4>
                <p className="text-neutral-900">
                  {page.updated_at ? new Date(page.updated_at).toLocaleDateString('pt-BR') : t.pageDetails.never}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Access Chart */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-neutral-600" />
              <h3 className="text-lg font-semibold text-neutral-900">
                {t.pageDetails.accessesInPeriod.replace("{period}", t.pageDetails.periods[selectedPeriod])}
              </h3>
            </div>

            {/* Segmented Control */}
            <div className="flex rounded-xl bg-neutral-100 p-1">
              {PERIOD_OPTIONS.map((period) => (
                <Button
                  key={period.value}
                  variant="tab"
                  active={selectedPeriod === period.value}
                  onClick={() => setSelectedPeriod(period.value)}
                  className="px-3 py-1.5 text-sm font-medium"
                >
                  {t.pageDetails.periods[period.value]}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {/* Chart */}
            <AccessChart data={accessData} />

            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-neutral-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-neutral-900">
                  {total}
                </div>
                <div className="text-sm text-neutral-500">{periodLabels.total}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-neutral-900">
                  {average}
                </div>
                <div className="text-sm text-neutral-500">{averageLabel}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-black">
                  {page.today_accesses || 0}
                </div>
                <div className="text-sm text-neutral-500">{t.pageDetails.today}</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
